import { ConfigurationStatusTag } from '@Components/SensorConfigurationsTable/ConfigurationStatusTag';
import TableCellWithLoading from '@Components/SensorConfigurationsTable/TableCellWithLoading';
import { TFunc } from '@Translations';
import { Configuration, FLAGS } from '@Types';

export type STATUS = 'idle' | 'pending' | 'success' | 'error';

export interface TableCellWithLoader {
	isLoading?: boolean;
	value?: string | number;
}

// Corresponds to keys in Configurations response object
export enum SENSOR_CONFIGURATIONS_COLUMNS_IDS {
	SENSOR_ID = 'sensorId',
	SENSOR_ALIAS = 'sensorAlias',
	SENSOR_TYPE = 'sensorType',
	UNIT = 'unit',
	LAST_VALUE = 'lastValue',
	STATUS = 'status',
	COLLECTION_INTERVAL = 'collectionInterval',
	ASSET_ID = 'assetId',
	LATITUDE = 'latitude',
	LONGITUDE = 'longitude',
	DATA_SOURCE_TYPE = 'dataSourceType',
	IS_PHYSICAL = 'isPhysical',
	LAST_VALUE_TIMESTAMP = 'lastValueTimestamp',
	TANK_CURVE_ID = 'tankCurveId',
	TANK_TYPE = 'tankType',
	// DATA_SOURCE_NAME = 'dataSourceName',
	SENSOR_TABLE = 'sensorTable',
	SENSOR_CHANNEL_FIELD = 'sensorChannelField',
	SENSOR_DATE_FIELD = 'sensorDateField',
	SENSOR_ID_FIELD = 'sensorIdField',
	TAG_VALUE = 'tagValue',
	TANK_CURVE_NAME = 'tankCurveName',
	TANK_DIAMETER = 'tankDiameter',
	TANK_CURVE_MAX_LEVEL = 'tankCurveMaxLevel',
	TANK_OFFSET = 'tankOffset',
}

export const SENSOR_CONFIGURATIONS_COLUMNS_HEADERS = (
	t: TFunc,
): {
	[key in SENSOR_CONFIGURATIONS_COLUMNS_IDS]: {
		headerName: string;
		featureFlag?: string;
		hideable?: boolean;
		renderCell?: (params: { value?: string }) => JSX.Element;
		renderTableCellWithLoader?: (params: {
			value?: TableCellWithLoader;
		}) => JSX.Element;
	};
} => ({
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.SENSOR_ID]: {
		headerName: t('Sensor ID'),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.SENSOR_ALIAS]: {
		headerName: t('Alias'),
		hideable: false,
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.SENSOR_TYPE]: {
		headerName: t('Sensor Type'),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.UNIT]: { headerName: t('Unit') },
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.LAST_VALUE]: {
		headerName: t('Last value'),
		featureFlag: FLAGS.ENABLE_LAST_VALUE_FOR_SENSOR_CONFIGURATION,
		renderTableCellWithLoader: ({ value }) =>
			TableCellWithLoading({
				children: value?.value,
				isLoading: value?.isLoading,
			}),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.STATUS]: {
		headerName: t('Sensor Health'),
		featureFlag: FLAGS.ENABLE_HEALTH_STATUS_FOR_SENSOR_CONFIGURATION,
		renderTableCellWithLoader: ({ value }) =>
			TableCellWithLoading({
				children: ConfigurationStatusTag({
					status: value?.value as Configuration['status'],
				}),
				isLoading: value?.isLoading,
			}),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.COLLECTION_INTERVAL]: {
		headerName: t('Sampling interval'),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.ASSET_ID]: { headerName: t('Asset ID') },
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.LATITUDE]: { headerName: t('Latitude') },
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.LONGITUDE]: {
		headerName: t('Longitude'),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.DATA_SOURCE_TYPE]: {
		headerName: t('Data source type'),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.IS_PHYSICAL]: {
		headerName: t('Physical or Virtual'),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.LAST_VALUE_TIMESTAMP]: {
		headerName: t('Last Reading Date/Time'),
		featureFlag: FLAGS.ENABLE_LAST_READ_DATE_TIME_SENSOR_CONFIGURATION,
		renderTableCellWithLoader: ({ value }) =>
			TableCellWithLoading({
				children: value?.value,
				isLoading: value?.isLoading,
			}),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.TANK_CURVE_ID]: {
		headerName: t('Tank Curve Id'),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.TANK_TYPE]: {
		headerName: t('Tank Type'),
	},
	// [SENSOR_CONFIGURATIONS_COLUMNS_IDS.DATA_SOURCE_NAME]: {
	// 	headerName: t('Data Source Name'),
	// },
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.SENSOR_TABLE]: {
		headerName: t('Sensor Table'),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.SENSOR_CHANNEL_FIELD]: {
		headerName: t('Sensor Channel Field'),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.SENSOR_DATE_FIELD]: {
		headerName: t('Sensor Date Field'),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.SENSOR_ID_FIELD]: {
		headerName: t('Sensor Id Field'),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.TAG_VALUE]: {
		headerName: t('Tag Value'),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.TANK_CURVE_NAME]: {
		headerName: t('Tank Curve Name'),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.TANK_DIAMETER]: {
		headerName: t('Tank Diameter'),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.TANK_CURVE_MAX_LEVEL]: {
		headerName: t('Tank Curve Max Level'),
	},
	[SENSOR_CONFIGURATIONS_COLUMNS_IDS.TANK_OFFSET]: {
		headerName: t('Tank Offset'),
	},
});

export enum PhysicalDataSourceType {
	ODBC = 'ODBC',
	OPCUA = 'OPC-UA',
	OLEDB = 'OLEDB',
}

export enum VirtualDataSourceType {
	Form = 'FORM',
	EMAGIN = 'emagin',
	CUSTOM_ANALYTICS = 'custom analytics',
	ILI = 'ili',
	MASS_BALANCE = 'mass balance',
	BULK_UPLOAD = 'Bulk Upload',
}

export const ALLOWED_BULK_DELETE_DATA_SOURCE_TYPES = [
	'physical',
	PhysicalDataSourceType.ODBC,
	PhysicalDataSourceType.OPCUA,
	PhysicalDataSourceType.OLEDB,
	VirtualDataSourceType.Form,
	VirtualDataSourceType.BULK_UPLOAD,
];

export const ALLOWED_SENSOR_TEST_DATA_SOURCE_TYPES = {
	inTable: [
		'physical',
		PhysicalDataSourceType.ODBC,
		PhysicalDataSourceType.OLEDB,
	],
};

export const DataSourceTypesLabels = {
	[VirtualDataSourceType.Form]: 'Form',
	[VirtualDataSourceType.CUSTOM_ANALYTICS]: 'Custom Analytics',
	[VirtualDataSourceType.ILI]: 'ILI',
	[VirtualDataSourceType.MASS_BALANCE]: 'Mass Balance',
	[VirtualDataSourceType.BULK_UPLOAD]: 'Bulk Upload',
};
